<template>
  <v-flex
    v-if="!loading"
    xs8
    offset-xs2
    align-self-center
    justify-center
  >
  <div>
    <v-container fill-height fluid>
    <v-row>
      <v-flex xs3 class="mr-2">
        <Search
          v-model="filters.search"
          :label="$t('SEARCH')"
          prepend-icon="search"
          :delay="500"
        />
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn
        v-if="can('create editors')"
        @click="showEditorModal(0)"
        color="primary"
        dark
        class="mb-2 mr-2"
      >{{ $t("NEW") }}
      </v-btn>
    </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="totalItems"
      :no-data-text="$t('NO_DATA')"
      :no-results-text="$t('NO_DATA')"
      hide-default-footer
      :options.sync="pagination"
      :loading="loading"
      class="elevation-1"
    >
      <template v-slot:item.name="{ item }">
        <router-link :to="{ name: routeName.EDITOR_GROUP_SHOW, params: { id: item.id } }">
            {{ item.name }}
        </router-link>
      </template>
      <template v-slot:item.updated_at="{ item }">
            {{ item.updated_at | timeFormat }}
      </template>
      <template v-slot:item.actions="{ item }">
            <v-layout row wrap>
              <v-flex class="text-no-wrap">
                <v-btn
                  icon
                  color="blue"
                  v-if="can('edit editors')"
                  @click="showEditorModal(item.id)"
                >
                  <v-icon>edit</v-icon>
                </v-btn>
                <v-btn
                  v-if="can('delete editors')"
                  icon
                  color="red"
                  @click="deleteEditorGroup(item.id)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
      </template>
    </v-data-table>
    <Paginator
      :total-items="totalItems"
      :page.sync="pagination.page"
      :items-per-page.sync="pagination.itemsPerPage"
      @change="fetchEditorsGroup"
    />
  </div>
  </v-flex>
  <Loader v-else/>
</template>
<script>
  import { mapActions } from 'vuex';

  import { editorsGroupService } from '../../utils/services/editorsGroup.service';
  import Search from '../../components/shared/Inputs/Search';
  import Loader from '../../components/shared/Loader';
  import EditorGroupModal from '../../components/modals/Editors/EditorGroupModal.vue';
  import Paginator from '../../components/shared/Paginator';

  export default {
    name: 'EditorGroupList',
    components: { Search, Loader, Paginator },
    data() {
      return {
        loading: false,
        items: [],
        totalItems: 0,
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortDesc: [false],
          sortBy: ['name'],
        },
        headers: [
          { text: this.$t('fields.name'), value: 'name' },
          { text: this.$t('fields.actions'), value: 'actions', sortable: false, align: 'center' },
        ],
        filters: {
          search: null,
        },
      };
    },
    computed: {
      requestFilters() {
        const filters = {
          search: this.filters.search,
        };
        if (this.pagination.sortBy.length === 1 && this.pagination.sortDesc.length === 1) {
          const { sortBy, sortDesc } = this.pagination;
          filters.sortBy = sortBy;
          filters.sortDesc = sortDesc;
        }
        return filters;
      },
    },
    methods: {
      ...mapActions('confirm', ['showConfirm']),
      ...mapActions('modal', ['showModal', 'hideModal']),
      ...mapActions('alert', ['showToast']),
      async fetchEditorsGroup(forcePage) {
        this.loading = true;
        const { page, itemsPerPage } = this.pagination;
        return editorsGroupService.index({
          page: page || forcePage || 1,
          itemsPerPage,
          filters: this.requestFilters,
        })
          .then(response => {
            this.items = response.data.data;
            this.totalItems = response.data.meta.total;
            this.pagination = this.getPagination(
              response.data.meta,
              { oldPagination: this.pagination, forcePage },
            );
          })
          .catch(error => {
            console.log(error);
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      exportData() {
        this.loading = true;
        return editorsGroupService.exportData({
          filters: this.requestFilters,
        })
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'editors.csv');
            document.body.appendChild(link);
            link.click();
          })
          .catch(error => {
            this.handleError(error, this.errors);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      async showEditorModal(id) {
        this.showModal({
          component: EditorGroupModal,
            data: {
              stored: {
                id,
              },
          },
          options: {
            title: this.$t('EDITOR'),
            onSave: (
              form,
              error,
            ) => editorsGroupService.save(form)
            .then(() => {
              this.hideModal({});
              this.showToast({ type: 'success', message: this.$t('SAVED') });
            })
            .catch(error => {
              this.handleError(error, this.errors);
            })
            .finally(() => {
              this.fetchEditorsGroup(this.pagination.page);
            }),
          },
        });
      },
      async deleteEditorGroup(id) {
          this.showConfirm({
            onConfirm: () => {
              this.loading = true;
              return editorsGroupService.remove(id)
              .then(() => {
                this.showToast({ type: 'success', message: this.$t('DELETED') });
                this.fetchEditorsGroup(this.pagination.page);
              })
              .catch(error => {
                this.handleError(error, this.errors);
              })
              .finally(() => {
                this.loading = false;
              });
            },
          });
      },
    },
    async created() {
      await this.fetchEditorsGroup(this.pagination.page);
    },
    watch: {
      filters: {
        deep: true,
        handler() {
          this.fetchEditorsGroup(1);
        },
      },
      'pagination.sortBy': {
        handler(oldVal, newVal) {
          if (newVal !== oldVal) {
            this.fetchEditorsGroup(1);
          }
        },
      },

    },
  };
</script>
